import React from 'react';
import {Box, Button, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField,} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {pl} from 'date-fns/locale';
import {Search as SearchIcon} from '@mui/icons-material';
import NewLeadButton from './NewLeadButton';

interface LeadSearchBarProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  acquisitionTypeFilter: string;
  setAcquisitionTypeFilter: (value: string) => void;
  offerFilter: number | '';
  setOfferFilter: (value: number | '') => void;
  createdAtFromFilter: Date | null;
  setCreatedAtFromFilter: (value: Date | null) => void;
  createdAtToFilter: Date | null;
  setCreatedAtToFilter: (value: Date | null) => void;
  setPage: (value: number) => void;
  refreshLeads: () => Promise<void>;
  acquisitionTypes: any[] | undefined;
  offers: any[] | undefined;
}

const LeadSearchBar: React.FC<LeadSearchBarProps> = ({
  searchTerm,
  setSearchTerm,
  acquisitionTypeFilter,
  setAcquisitionTypeFilter,
  offerFilter,
  setOfferFilter,
  createdAtFromFilter,
  setCreatedAtFromFilter,
  createdAtToFilter,
  setCreatedAtToFilter,
  setPage,
  refreshLeads,
  acquisitionTypes,
  offers
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        mb: 3
      }}
    >
      {/* Pierwsza linia filtrów */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {/* Nowy lead */}
        <Box sx={{ width: '160px' }}>
          <NewLeadButton refreshLeads={refreshLeads} />
        </Box>


        {/* Wyszukiwarka */}
        <TextField
          placeholder="Szukaj kontaktów... "
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ pl: 1 }}>
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{
            flexGrow: 1,
            minWidth: '200px',
            fontWeight: 400,
            textTransform: 'none',
            '& .MuiOutlinedInput-root': {
              borderRadius: '999px',
              height: '36px',
              paddingLeft: '4px',
              overflow: 'hidden',
              backgroundColor: '#fff',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: '999px',
            },
            '& .MuiInputBase-input': {
              paddingY: '8px',
              color: '#000',
              fontSize: '0.9rem',
            },
            '& input.MuiInputBase-input::placeholder': {
              color: '#666',
              opacity: 1,
              fontSize: '0.9rem',
            }
          }}
        />
      </Box>

      {/* Druga linia filtrów */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {/* Źródło pozyskania */}
        <FormControl
          size="small"
          sx={{
            width: '160px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '999px',
              height: '36px',
              backgroundColor: '#fff',
              paddingLeft: '12px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: '999px',
            },
            '& .MuiSelect-select': {
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              height: '36px',
              paddingY: '8px',
            }
          }}
        >
          <InputLabel
            id="acquisition-type-filter-label"
            sx={{ fontSize: '0.8rem' }}
          >
            Źródło
          </InputLabel>
          <Select
            labelId="acquisition-type-filter-label"
            value={acquisitionTypeFilter}
            label="Źródło"
            onChange={(e) => {
              setAcquisitionTypeFilter(e.target.value as string);
              setPage(0);
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {acquisitionTypes?.map(type => (
              <MenuItem key={type.id} value={type.id.toString()}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Oferta/Tag */}
        <FormControl
          size="small"
          sx={{
            width: '160px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '999px',
              height: '36px',
              backgroundColor: '#fff',
              paddingLeft: '12px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: '999px',
            },
            '& .MuiSelect-select': {
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              height: '36px',
              paddingY: '8px',
            }
          }}
        >
          <InputLabel
            id="offer-filter-label"
            sx={{ fontSize: '0.8rem' }}
          >
            Oferta
          </InputLabel>
          <Select
            labelId="offer-filter-label"
            value={offerFilter}
            label="Oferta"
            onChange={(e) => {
              setOfferFilter(e.target.value as number | '');
              setPage(0);
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {offers?.map(offer => (
              <MenuItem key={offer.id} value={offer.id}>
                {offer.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Data utworzenia od-do */}
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
          <DatePicker
            label="Utworzono od"
            value={createdAtFromFilter}
            onChange={(date) => {
              setCreatedAtFromFilter(date);
              setPage(0);
            }}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  width: '160px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '999px',
                    height: '36px',
                    backgroundColor: '#fff',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '999px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.8rem',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.8rem',
                    paddingY: '8px',
                  }
                }
              }
            }}
          />
          <DatePicker
            label="Utworzono do"
            value={createdAtToFilter}
            onChange={(date) => {
              setCreatedAtToFilter(date);
              setPage(0);
            }}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  width: '160px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '999px',
                    height: '36px',
                    backgroundColor: '#fff',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '999px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.8rem',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.8rem',
                    paddingY: '8px',
                  }
                }
              }
            }}
          />
        </LocalizationProvider>

        {/* Wyczyść filtry */}
        {(searchTerm || acquisitionTypeFilter !== '' || offerFilter !== '' ||
          createdAtFromFilter !== null || createdAtToFilter !== null) && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSearchTerm('');
              setAcquisitionTypeFilter('');
              setOfferFilter('');
              setCreatedAtFromFilter(null);
              setCreatedAtToFilter(null);
              setPage(0);
            }}
            sx={{
              width: '160px',
              height: '36px',
              borderRadius: '999px',
              whiteSpace: 'nowrap',
              fontWeight: 400,
              textTransform: 'none',
              fontSize: '0.85rem'
            }}
          >
            Wyczyść filtry
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default LeadSearchBar;